import { useCallback, useRef, useState, useTransition } from "react";
import { useStableCallback } from "@business-finland/wif-ui-lib";

export enum MutationStatus {
  INIT = "init",
  LOADING = "loading",
  SUCCESS = "success",
  ERROR = "error",
  CANCELLED = "cancelled",
}

export interface IMutationOptions<MutationData> {
  /** Callback executed when query's fetch is initiated. */
  onLoading?: () => void;
  /** Callback executed when query is successful. */
  onSuccess?: (data: MutationData) => void;
  /** Callback executed when query has failed. */
  onError?: (error: Error) => void;
  /** Callback executed when query in finished (success or fail) */
  onDone?: () => void;
}

export interface IMutationResult<MutationData = unknown> {
  /** Current status of the query.  */
  status: MutationStatus;
  /** Init state. The query has not yet started. It might be using initialData.  */
  isInit: boolean;
  /** Success state */
  isSuccess: boolean;
  /** Resulting data. Can be undefined */
  data?: MutationData;
  /** Error state */
  isError: boolean;
  /** Error message. */
  errorMsg?: string;
  /** Loading state */
  isLoading: boolean;
  /** Callback to refetch the current url. */
  mutate: (options?: RequestInit) => void;
  /** Callback to cancel ongoing fetch. */
  cancel: () => void;
  /** if query is disabled */
  isDisabled?: boolean;
}

/**
 * Hook to handle fetch requests with error handling and cache.
 *
 * @examples
 * - default
 *   ```tsx
 *   const { mutate } = useMutation<JobsData>("/jobs");
 *   ```
 */
export default function useMutation<MutationData>(
  url: string | URL,
  options: IMutationOptions<MutationData> = {}
): IMutationResult<MutationData> {
  const mutationUrl = url.toString();
  const { onLoading, onSuccess, onError, onDone } = options;
  const abortControllerRef = useRef<AbortController>();

  const [, startTransition] = useTransition();
  const [status, setStatus] = useState<MutationStatus>(MutationStatus.INIT);
  const [data, setData] = useState<MutationData | undefined>(undefined);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const handleLoading = useStableCallback(() => {
    startTransition(() => {
      setErrorMsg(undefined);
    });

    startTransition(() => {
      onLoading?.();
      setStatus(MutationStatus.LOADING);
    });
  });

  const handleSuccess = useStableCallback((data: any) => {
    startTransition(() => {
      onSuccess?.(data);
      setData(data);
      setErrorMsg(undefined);
      setStatus(MutationStatus.SUCCESS);
    });
  });

  const handleError = useStableCallback((error: Error) => {
    startTransition(() => {
      setErrorMsg(error.message);
      const errorStatus =
        error.name === "AbortError"
          ? MutationStatus.CANCELLED
          : MutationStatus.ERROR;
      setStatus(errorStatus);

      if (errorStatus === MutationStatus.ERROR && onError) {
        onError(error);
      }
    });
  });

  const handleDone = useStableCallback(() => onDone?.());

  const mutate = useCallback(
    async (options: RequestInit = {}): Promise<void> => {
      // cancel the previous request before new request
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      handleLoading();

      const handleResponse = async (response: Response) => {
        const data = await response.json();
        if (abortController.signal.aborted)
          throw new DOMException(
            "Aborted while handling response",
            "AbortError"
          );

        return data;
      };

      const request = new Request(mutationUrl, {
        ...options,
        signal: abortController.signal,
      });

      fetch(request)
        .then(handleResponse)
        .then(handleSuccess)
        .catch(handleError)
        .finally(handleDone);
    },
    [mutationUrl, handleLoading, handleSuccess, handleError, handleDone]
  );

  const cancelMutation = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return {
    status,
    data,
    errorMsg,
    mutate,
    cancel: cancelMutation,
    isInit: status === MutationStatus.INIT,
    isSuccess: status === MutationStatus.SUCCESS,
    isError: status === MutationStatus.ERROR,
    isLoading: status === MutationStatus.LOADING,
  };
}
